/* ═══════════════════════════════════════════════════════════
   ASTROITE GAMING — API Client Service
   Handles all communication with Catalyst backend
   ═══════════════════════════════════════════════════════════ */

// Catalyst serves functions at /server/<functionName>/ in both local and deployed
const AUTH_URL = '/server/authAPI';
const GAME_URL = '/server/gameAPI';
const ADMIN_URL = '/server/adminAPI';
const SESSION_URL = '/server/sessionAPI'; // Catalyst Embedded Auth resolver (catalyst mode)

const TOKEN_KEY = 'astroite_token';
const REFRESH_KEY = 'astroite_refresh_token';
const USER_KEY = 'astroite_user';

// Auth mode (build-time flag). 'legacy' = custom OTP/JWT (default, unchanged);
// 'catalyst' = Catalyst Embedded Auth — identity rides the same-origin session
// cookie, so API calls use credentials:'include' instead of the X-Auth-Token JWT.
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'legacy';
const CATALYST_MODE = AUTH_MODE === 'catalyst';

// ── Token Management ─────────────────────────────────────

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(accessToken, refreshToken) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }
  // Mark this browser session as active (cleared when browser closes)
  sessionStorage.setItem('astroite_session_active', 'true');
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('astroite_keep_logged_in');
  sessionStorage.removeItem('astroite_session_active');
}

export function getStoredUser() {
  try {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function isLoggedIn() {
  // Catalyst Embedded Auth: identity rides the same-origin session cookie, so
  // there is NO local token. Any non-guest who reached the app is authenticated
  // (App init only lands authenticated users or guests past the login screen);
  // API calls carry the cookie and the backend stays the source of truth (401
  // if the session is gone). Without this, the game's session/sync/end calls —
  // all gated on isLoggedIn() — silently no-op and nothing is ever persisted.
  if (CATALYST_MODE) {
    return !isGuest();
  }
  const token = getToken();
  if (!token) return false;
  // If user opted out of "Keep me logged in", check sessionStorage marker
  const keepLoggedIn = localStorage.getItem('astroite_keep_logged_in');
  if (keepLoggedIn === 'false' && !sessionStorage.getItem('astroite_session_active')) {
    // Session ended (browser was closed) — clear tokens
    clearTokens();
    return false;
  }
  return true;
}

export function isGuest() {
  return localStorage.getItem('astroite_guest') === 'true';
}

export function setGuest(val) {
  if (val) {
    localStorage.setItem('astroite_guest', 'true');
  } else {
    localStorage.removeItem('astroite_guest');
  }
}

// ── HTTP Fetch Wrapper ────────────────────────────────────

async function request(method, path, body, requiresAuth = true, baseUrl = AUTH_URL) {
  const headers = { 'Content-Type': 'application/json' };
  const options = { method, headers };

  if (CATALYST_MODE) {
    // Same-origin Catalyst session cookie carries identity — no token header.
    options.credentials = 'include';
  } else if (requiresAuth) {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    headers['X-Auth-Token'] = token;
  }

  if (body) options.body = JSON.stringify(body);

  const response = await fetch(`${baseUrl}${path}`, options);

  // Legacy-only JWT refresh on 401. In catalyst mode Catalyst owns the session
  // (cookie), so a 401 surfaces to the caller to re-resolve (re-run init/sign-in).
  if (!CATALYST_MODE && response.status === 401 && requiresAuth) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers['X-Auth-Token'] = getToken();
      const retry = await fetch(`${baseUrl}${path}`, { method, headers, body: options.body });
      return handleResponse(retry);
    } else {
      clearTokens();
      throw new Error('Session expired');
    }
  }

  return handleResponse(response);
}

async function handleResponse(response) {
  // Parse the body defensively — a crashed backend may return HTML/text, not JSON.
  const raw = await response.text();
  let data = null;
  try { data = raw ? JSON.parse(raw) : null; } catch { /* non-JSON body */ }

  if (!response.ok) {
    const serverMsg = data && data.error;
    const message = serverMsg
      || (raw && raw.slice(0, 200))
      || `कुछ गलत हो गया | Request failed (${response.status})`;
    // Surface the real failure for debugging instead of hiding it.
    console.error(`API ${response.status} ${response.url}:`, serverMsg || raw);
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return data;
}

async function tryRefreshToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${AUTH_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });
    if (!response.ok) return false;
    const data = await response.json();
    setTokens(data.token, data.refreshToken);
    return true;
  } catch {
    return false;
  }
}

// ── Auth API ──────────────────────────────────────────────

export async function sendEmailOtp(identifier) {
  return request('POST', '/api/auth/send-otp', { identifier }, false);
}

export async function verifyEmailOtp(identifier, otp) {
  const data = await request('POST', '/api/auth/verify-otp', { identifier, otp }, false);
  if (data.token) {
    setTokens(data.token, data.refreshToken);
    if (data.user) setStoredUser(data.user);
  }
  return data;
}

export async function login({ email, password }) {
  const data = await request('POST', '/api/auth/login', { email, password }, false);
  setTokens(data.token, data.refreshToken);
  setStoredUser(data.user);
  return data;
}

export async function registerWithEmail({ name, email, phone, password, verifyToken }) {
  const data = await request('POST', '/api/auth/register',
    { name, email, phone, password, verify_token: verifyToken }, false);
  setTokens(data.token, data.refreshToken);
  setStoredUser(data.user);
  return data;
}

export async function logout() {
  try {
    await request('POST', '/api/auth/logout');
  } catch {
    // Logout locally even if server fails
  }
  clearTokens();
  setGuest(false);
}

export async function getProfile() {
  if (CATALYST_MODE) {
    // Catalyst owns identity — resolve via sessionAPI (find-or-link), not authAPI's JWT route.
    return request('GET', '/api/auth/me', null, true, SESSION_URL);
  }
  const data = await request('GET', '/api/auth/me');
  setStoredUser(data.user);
  return data;
}

// ── Game API (Phase 2) ──────────────────────────────────

export async function getGameState() {
  return request('GET', '/api/game/state', null, true, GAME_URL);
}

export async function startSession() {
  return request('POST', '/api/game/session/start', null, true, GAME_URL);
}

export async function syncSession(sessionId, ramDelta, obstacles) {
  return request('POST', '/api/game/session/sync', {
    session_id: sessionId, ram_delta: ramDelta, obstacles
  }, true, GAME_URL);
}

export async function endSession(sessionId, finalCount, gameOverReason = 'collision') {
  return request('POST', '/api/game/session/end', {
    session_id: sessionId, final_count: finalCount, game_over_reason: gameOverReason
  }, true, GAME_URL);
}

export async function getLeaderboard() {
  return request('GET', '/api/game/leaderboard', null, true, GAME_URL);
}

// ── Peeth API (Phase 3) ─────────────────────────────────

export async function getPeeths() {
  return request('GET', '/api/peeths', null, true, GAME_URL);
}

export async function getPeeth(id) {
  return request('GET', `/api/peeths/${id}`, null, true, GAME_URL);
}

export async function getUserPeethProgress() {
  return request('GET', '/api/peeths/user/progress', null, true, GAME_URL);
}

export async function markPeethViewed(id) {
  return request('POST', `/api/peeths/${id}/view`, null, true, GAME_URL);
}

// ── Jyotirlinga API ─────────────────────────────────────

export async function getJyotirlingas() {
  return request('GET', '/api/jyotirlingas', null, true, GAME_URL);
}

export async function getJyotirlinga(id) {
  return request('GET', `/api/jyotirlingas/${id}`, null, true, GAME_URL);
}

export async function markJyotirlingaViewed(id) {
  return request('POST', `/api/jyotirlingas/${id}/view`, null, true, GAME_URL);
}

// ── Admin API (Phase 4) ─────────────────────────────────

export async function getAdminStats() {
  return request('GET', '/api/admin/stats', null, true, ADMIN_URL);
}

export async function getAdminUsers(page = 1, limit = 20) {
  return request('GET', `/api/admin/users?page=${page}&limit=${limit}`, null, true, ADMIN_URL);
}

export async function getAdminUser(userId) {
  return request('GET', `/api/admin/users/${userId}`, null, true, ADMIN_URL);
}

export async function updateAdminPeeth(peethId, data) {
  return request('PUT', `/api/admin/peeths/${peethId}`, data, true, ADMIN_URL);
}

export async function seedPeethsAdmin() {
  return request('POST', '/api/admin/seed-peeths', null, true, ADMIN_URL);
}
